import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Award, Medal, Edit } from 'lucide-react';
import { toast } from 'sonner';

const TournamentPerformance = ({ userId, className = '' }) => {
  const { API, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    daily: { total: 0, finals: 0, semi_finals: 0, wins: 0 },
    knockout: { total: 0, finals: 0, semi_finals: 0, wins: 0 },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userId) fetchStats();
  }, [userId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/tournament-performance`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch tournament performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = () => {
    setForm({
      daily: { ...stats.daily },
      knockout: { ...stats.knockout },
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${userId}/override-tournament-performance`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('עודכן בהצלחה');
      setEditOpen(false);
      fetchStats();
    } catch (e) {
      toast.error('שגיאה בעדכון');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('להחזיר לחישוב אוטומטי? כל השינויים הידניים יימחקו.')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${userId}/override-tournament-performance`,
        { reset: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('חזר לחישוב אוטומטי');
      setEditOpen(false);
      fetchStats();
    } catch (e) {
      toast.error('שגיאה');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader><CardTitle>ביצועים בטורנירים</CardTitle></CardHeader>
        <CardContent><div className="text-center text-gray-500">טוען...</div></CardContent>
      </Card>
    );
  }
  if (!stats) return null;

  const isManual = !!stats.manual_override;

  const StatRow = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center justify-between p-2">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-sm text-gray-700">{label}:</span>
      </div>
      <span className="font-bold text-gray-900">{value}</span>
    </div>
  );

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              ביצועים בטורנירים
              {isManual && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">ידני</span>}
            </CardTitle>
            {user && user.is_admin && (
              <Button
                size="sm"
                variant="outline"
                data-testid="edit-tournament-perf-btn"
                onClick={openEdit}
                className="h-7 px-2 border-purple-400 text-purple-600 hover:bg-purple-50"
              >
                <Edit className="w-3.5 h-3.5 ml-1" />
                <span className="text-xs">ערוך</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <h3 className="font-bold text-md text-center text-blue-700 border-b-2 border-blue-200 pb-2 mb-3">טורנירים יומיים</h3>
              <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                <StatRow icon={Trophy} label="השתתפויות" value={stats.daily.total} color="text-blue-600" />
                <StatRow icon={Award} label="גמרים" value={stats.daily.finals} color="text-yellow-600" />
                <StatRow icon={Medal} label="חצאי גמר" value={stats.daily.semi_finals} color="text-orange-600" />
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-blue-700">🏆 זכיות:</span>
                    <span className="text-2xl font-bold text-blue-700">{stats.daily.wins}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-bold text-md text-center text-purple-700 border-b-2 border-purple-200 pb-2 mb-3">טורנירי נוקאאוט</h3>
              <div className="bg-purple-50 rounded-lg p-3 space-y-1">
                <StatRow icon={Trophy} label="השתתפויות" value={stats.knockout.total} color="text-purple-600" />
                <StatRow icon={Award} label="גמרים" value={stats.knockout.finals} color="text-yellow-600" />
                <StatRow icon={Medal} label="חצאי גמר" value={stats.knockout.semi_finals} color="text-orange-600" />
                <div className="border-t border-purple-200 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-purple-700">🏆 זכיות:</span>
                    <span className="text-2xl font-bold text-purple-700">{stats.knockout.wins}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(stats.daily.wins > 0 || stats.knockout.wins > 0) && (
            <div className="mt-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg text-center">
              <p className="text-sm font-bold text-orange-700">
                🏆 סה"כ {stats.daily.wins + stats.knockout.wins} זכיות בטורנירים!
              </p>
            </div>
          )}

          {stats.daily.total === 0 && stats.knockout.total === 0 && (
            <div className="mt-4 text-center text-gray-500 py-4">
              <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>עדיין לא השתתפת בטורנירים</p>
            </div>
          )}
        </CardContent>
      </Card>

      {editOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setEditOpen(false)}>
          <div className="bg-white rounded-lg max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">עריכת ביצועים בטורנירים</h3>
              <button onClick={() => setEditOpen(false)} className="text-2xl">×</button>
            </div>
            
            {[
              { key: 'daily', label: 'טורנירים יומיים', color: 'blue' },
              { key: 'knockout', label: 'טורנירי נוקאאוט', color: 'purple' }
            ].map(({ key, label, color }) => (
              <div key={key} className={`mb-4 p-3 border-2 border-${color}-200 rounded-lg`}>
                <h4 className={`font-bold mb-2 text-${color}-700`}>{label}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {['total', 'finals', 'semi_finals', 'wins'].map((field) => (
                    <label key={field} className="text-sm">
                      {field === 'total' ? 'השתתפויות' : field === 'finals' ? 'גמרים' : field === 'semi_finals' ? 'חצאי גמר' : 'זכיות'}:
                      <input
                        type="number"
                        min="0"
                        data-testid={`${key}-${field}-input`}
                        value={form[key][field]}
                        onChange={(e) => setForm({ ...form, [key]: { ...form[key], [field]: parseInt(e.target.value) || 0 } })}
                        className="w-full px-3 py-1 border rounded mt-1"
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
            
            <div className="flex justify-between gap-2 mt-4">
              <Button
                onClick={handleReset}
                disabled={saving}
                variant="outline"
                className="text-orange-600 border-orange-400"
                data-testid="reset-tournament-perf-btn"
              >
                חזרה לחישוב אוטומטי
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>ביטול</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-tournament-perf-btn">
                  {saving ? 'שומר...' : 'שמור'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TournamentPerformance;
